/**
 * Tracks whether any chat / LLM query is currently in flight, so chrome
 * elements outside the page (e.g. the HeaderActivityStrip below the navbar)
 * can react.
 *
 * Usage:
 *   - Wrap protected routes in <ChatActivityProvider>.
 *   - Chat hooks call useChatActivity().setActive(true|false) as queries
 *     start and finish.
 *   - Chrome consumers (HeaderActivityStrip, etc.) call useChatActivity()
 *     and read .isActive to drive their own animations.
 *
 * State shape is intentionally tiny: a single boolean + setter. Adding more
 * structure (e.g. concurrent-query counter) can come later if multiple
 * surfaces start querying at once.
 */

import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

interface ChatActivityValue {
  isActive: boolean;
  setActive: (next: boolean) => void;
}

const ChatActivityContext = createContext<ChatActivityValue>({
  isActive: false,
  setActive: () => {
    /* default no-op — replaced by provider */
  },
});

export const ChatActivityProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isActive, setIsActive] = useState(false);

  const setActive = useCallback((next: boolean) => {
    setIsActive(next);
  }, []);

  const value = useMemo<ChatActivityValue>(() => ({ isActive, setActive }), [isActive, setActive]);

  return <ChatActivityContext.Provider value={value}>{children}</ChatActivityContext.Provider>;
};

export const useChatActivity = (): ChatActivityValue => useContext(ChatActivityContext);
